import { useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  styled,
  tableCellClasses,
} from "@mui/material";
import { Delete, Save } from "@mui/icons-material";
import {
  EC2Document,
  States,
  EC2Municipality,
} from "../../../../API/types/documentationCatalogs.types";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#003766",
    color: theme.palette.primary.contrastText,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  border: "1px solid #e0e0e0",
}));

export type SelectedDocument = {
  document: EC2Document;
  newName: string;
  state: States | null;
  municipality: EC2Municipality | null;
};

interface SelectedDocumentsTableProps {
  selectedDocuments: SelectedDocument[];
  onNameChange: (documentId: number, newName: string) => void;
  onRemove: (documentId: number) => void;
  onSubmit: () => void;
  loading: boolean;
}

const SelectedDocumentsTable = ({
  selectedDocuments,
  onNameChange,
  onRemove,
  onSubmit,
  loading,
}: SelectedDocumentsTableProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // 🟡 Effect - Scroll to bottom when a new document is added
  useEffect(() => {
    if (tableContainerRef.current && selectedDocuments.length > 0) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop =
            tableContainerRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [selectedDocuments.length]);

  if (selectedDocuments.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          Selected Documents ({selectedDocuments.length})
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Save />}
          onClick={onSubmit}
          disabled={loading}
          sx={{ backgroundColor: "#193F70" }}
        >
          {loading ? "Renaming..." : "Rename All"}
        </Button>
      </Box>
      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        sx={{
          maxHeight: 400,
          overflow: "auto",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "15%" }}>State</StyledTableCell>
              <StyledTableCell sx={{ width: "20%" }}>
                Municipality
              </StyledTableCell>
              <StyledTableCell sx={{ width: "25%" }}>
                Current Name
              </StyledTableCell>
              <StyledTableCell sx={{ width: "35%" }}>New Name</StyledTableCell>
              <StyledTableCell sx={{ width: "5%" }}>Remove</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedDocuments.map((item) => (
              <TableRow key={item.document.documentId}>
                <StyledTableCell>
                  <Typography
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.state?.name || "-"}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.municipality?.municipalityName || "-"}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.document.name}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={item.newName}
                    onChange={(e) =>
                      onNameChange(item.document.documentId, e.target.value)
                    }
                    placeholder="Enter new name"
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => onRemove(item.document.documentId)}
                  >
                    <Delete />
                  </IconButton>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SelectedDocumentsTable;
